/**
 * Decision Engine - Conditions Management
 *
 * Manages policy condition definitions for the decision engine.
 * v2 SafetyCulture format with categories, wording references and compliance consequences.
 */

import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { SectionHeader } from '@client/components/_ui/data-display/SectionHeader';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Badge, Form, Input, InputNumber, Switch, Tag } from 'antd';
import { ListChecks, Plus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

type ConditionFormInput = {
    conditionId: string;
    conditionName: string;
    category: string;
    appliesTo: string;
    wordingReference: string | null;
    summary: string | null;
    consequenceOfNonCompliance: string | null;
    policyWording: string | null;
    sortOrder: number;
    isActive: boolean;
};

const FormItem = Form.Item<ConditionFormInput>;

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/$productTypeId/decision-engine/conditions',
)({
    staticData: {
        metadata: {
            title: 'Conditions',
        },
    },
    component: ConditionsManagement,
});

function ConditionsManagement() {
    const { productTypeId } = useParams({ strict: false });
    const { trpc, trpcUtils } = useTrpc();
    const [form] = Form.useForm<ConditionFormInput>();

    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingId, setEditingId] = useState<bigint | null>(null);
    // The form mounts with the drawer, so the values it opens on are held here and passed as `initialValues`.
    const initialValuesRef = useRef<ConditionFormInput | null>(null);
    const formDirty = useFormDirty();
    const closeRef = useRef<(() => void) | null>(null);
    const formId = 'condition-form';

    // Fetch conditions
    const { data, isLoading } = trpc.claim.decisionEngine.admin.listConditions.useQuery(
        { productTypeId: BigInt(productTypeId!) },
        { enabled: !!productTypeId },
    );
    const conditions = data?.rows ?? [];

    // Mutations
    const { mutate: createCondition, isPending: isCreating } =
        trpc.claim.decisionEngine.admin.createCondition.useMutation({
            onSuccess() {
                message.success('Condition created.');
                void trpcUtils.claim.decisionEngine.admin.listConditions.invalidate();
                void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
                closeRef.current?.();
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    const { mutate: updateCondition, isPending: isUpdating } =
        trpc.claim.decisionEngine.admin.updateCondition.useMutation({
            onSuccess() {
                message.success('Condition updated.');
                void trpcUtils.claim.decisionEngine.admin.listConditions.invalidate();
                void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
                closeRef.current?.();
            },
            onError(error) {
                errorMessage.show(error);
            },
        });

    const { mutate: deleteCondition } = trpc.claim.decisionEngine.admin.deleteCondition.useMutation({
        onSuccess() {
            message.success('Condition deleted.');
            void trpcUtils.claim.decisionEngine.admin.listConditions.invalidate();
            void trpcUtils.claim.decisionEngine.getConfig.invalidate({ productTypeId: BigInt(productTypeId!) });
        },
        onError(error) {
            errorMessage.show(error);
        },
    });

    // Table columns
    const columnHelper = createColumnHelper<(typeof conditions)[number]>();

    const columns = useMemo(
        () => [
            columnHelper.accessor('conditionId', {
                id: 'conditionId',
                header: 'ID',
                width: 120,
                cell: ({ row }) => <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{row.conditionId}</code>,
            }),
            columnHelper.accessor('conditionName', {
                id: 'conditionName',
                header: 'Name',
                width: 220,
            }),
            columnHelper.accessor('category', {
                id: 'category',
                header: 'Category',
                width: 160,
                cell: ({ row }) => <Tag>{row.category}</Tag>,
            }),
            columnHelper.accessor('appliesTo', {
                id: 'appliesTo',
                header: 'Applies to',
                width: 200,
                cell: ({ row }) => {
                    const truncated = row.appliesTo.length > 60 ? `${row.appliesTo.slice(0, 60)}...` : row.appliesTo;
                    return <span title={row.appliesTo}>{truncated}</span>;
                },
            }),
            columnHelper.accessor('summary', {
                id: 'summary',
                header: 'Summary',
                width: 200,
                cell: ({ row }) => {
                    if (!row.summary) return <span className="text-muted-foreground">—</span>;
                    const truncated = row.summary.length > 60 ? `${row.summary.slice(0, 60)}...` : row.summary;
                    return <span title={row.summary}>{truncated}</span>;
                },
            }),
            columnHelper.accessor('isActive', {
                id: 'isActive',
                header: 'Status',
                width: 100,
                cell: ({ row }) => (
                    <Badge status={row.isActive ? 'success' : 'default'} text={row.isActive ? 'Active' : 'Inactive'} />
                ),
            }),
            columnHelper.actions({
                id: 'actions',
                cell: ({ row }) => (
                    <Space size="small">
                        <Button.Edit onClick={() => openEditDrawer(row)} />
                        <Button.Delete
                            confirmTitle="Delete condition"
                            confirmDescription="Are you sure you want to delete this condition?"
                            onConfirm={(onSuccess) =>
                                deleteCondition(
                                    { id: row.id },
                                    {
                                        onSuccess: () => {
                                            onSuccess();
                                        },
                                        onError: (error) => {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    </Space>
                ),
                fixed: 'right',
            }),
        ],
        [columnHelper, deleteCondition],
    );

    const tableProps = usePrismaTable({
        data: conditions,
        total: data?.total ?? 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        pagination: { position: 'none' },
        emptyState: { noRecordsTitle: 'No conditions yet' },
    });

    // Drawer handlers
    const openCreateDrawer = () => {
        setEditingId(null);
        initialValuesRef.current = {
            conditionId: '',
            conditionName: '',
            category: '',
            appliesTo: '',
            wordingReference: null,
            summary: null,
            consequenceOfNonCompliance: null,
            policyWording: null,
            sortOrder: 0,
            isActive: true,
        };
        setDrawerOpen(true);
    };

    const openEditDrawer = (condition: (typeof conditions)[number]) => {
        setEditingId(condition.id);
        initialValuesRef.current = {
            conditionId: condition.conditionId,
            conditionName: condition.conditionName,
            category: condition.category,
            appliesTo: condition.appliesTo,
            wordingReference: condition.wordingReference,
            summary: condition.summary,
            consequenceOfNonCompliance: condition.consequenceOfNonCompliance,
            policyWording: condition.policyWording,
            sortOrder: condition.sortOrder,
            isActive: condition.isActive,
        };
        setDrawerOpen(true);
    };

    // Runs after the closing animation, whether the drawer was dismissed or closed after a save.
    const resetDrawer = () => {
        form.resetFields();
        formDirty.markClean();
        initialValuesRef.current = null;
        setDrawerOpen(false);
        setEditingId(null);
    };

    const onFinish = (values: ConditionFormInput) => {
        const data = {
            conditionId: values.conditionId,
            conditionName: values.conditionName,
            category: values.category,
            appliesTo: values.appliesTo,
            wordingReference: values.wordingReference,
            summary: values.summary,
            consequenceOfNonCompliance: values.consequenceOfNonCompliance,
            policyWording: values.policyWording,
            sortOrder: values.sortOrder,
            isActive: values.isActive,
        };

        if (editingId) {
            updateCondition({ id: editingId, data });
        } else {
            createCondition({ productTypeId: BigInt(productTypeId!), data });
        }
    };

    return (
        <PageController route={Route}>
            <div className="mb-6 flex items-start gap-4 rounded-lg border border-cyan-200 bg-cyan-50 p-4 dark:border-cyan-800 dark:bg-cyan-950/30">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-cyan-100 dark:bg-cyan-900/50">
                    <ListChecks className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div>
                    <SectionHeader as="h3" size="small" title="Policy conditions" className="mb-1" />
                    <p className="text-muted-foreground text-sm">
                        Define policy conditions that must be met for coverage to apply. Each condition includes the
                        consequence of non-compliance, helping the AI assess whether conditions precedent are satisfied.
                    </p>
                </div>
            </div>
            <TableToolbar
                actions={
                    <Button type="primary" icon={<Plus />} onClick={openCreateDrawer}>
                        Add condition
                    </Button>
                }
            />
            <PrismaTable {...tableProps} />

            {drawerOpen && (
                <OverlayPage
                    title={editingId ? 'Edit condition' : 'Add condition'}
                    size="small"
                    isDirty={formDirty.isDirty}
                    afterClose={resetDrawer}
                    footer={({ requestClose }) => (
                        <DrawerFooter
                            onCancel={requestClose}
                            formId={formId}
                            okText={editingId ? 'Save changes' : 'Create'}
                            okLoading={isCreating || isUpdating}
                        />
                    )}
                >
                    {({ onClose }) => {
                        closeRef.current = onClose;
                        return (
                            <Form<ConditionFormInput>
                                form={form}
                                {...formDirty.formProps}
                                layout="vertical"
                                onFinish={onFinish}
                                id={formId}
                                clearOnDestroy
                                initialValues={initialValuesRef.current ?? undefined}
                            >
                                <FormItem
                                    label="Condition ID"
                                    name="conditionId"
                                    rules={[{ required: true, message: 'Condition ID is required' }]}
                                    tooltip="Unique identifier for this condition"
                                >
                                    <Input placeholder="e.g., COND001" />
                                </FormItem>

                                <FormItem
                                    label="Name"
                                    name="conditionName"
                                    rules={[{ required: true, message: 'Name is required' }]}
                                >
                                    <Input placeholder="e.g., Notification within 30 days" />
                                </FormItem>

                                <FormItem
                                    label="Category"
                                    name="category"
                                    rules={[{ required: true, message: 'Category is required' }]}
                                >
                                    <Input placeholder="e.g., Conditions precedent" />
                                </FormItem>

                                <FormItem
                                    label="Applies to"
                                    name="appliesTo"
                                    rules={[{ required: true, message: 'Applies to is required' }]}
                                >
                                    <Input placeholder="e.g., All insuring clauses" />
                                </FormItem>

                                <FormItem label="Wording reference" name="wordingReference">
                                    <Input placeholder="e.g., Section 6.1" />
                                </FormItem>

                                <FormItem label="Summary" name="summary">
                                    <Input.TextArea rows={3} placeholder="Brief summary of the condition" />
                                </FormItem>

                                <FormItem
                                    label="Consequence of non-compliance"
                                    name="consequenceOfNonCompliance"
                                    tooltip="What happens when this condition is not met"
                                >
                                    <Input.TextArea rows={3} placeholder="e.g., Coverage may be denied or reduced" />
                                </FormItem>

                                <FormItem label="Policy wording" name="policyWording">
                                    <Input.TextArea rows={3} placeholder="Relevant policy wording text" />
                                </FormItem>

                                <div className="mt-4 border-separator border-t pt-4">
                                    <FormItem label="Sort order" name="sortOrder" tooltip="Lower numbers appear first">
                                        <InputNumber min={0} className="w-full" />
                                    </FormItem>

                                    <FormItem label="Active" name="isActive" valuePropName="checked">
                                        <Switch />
                                    </FormItem>
                                </div>
                            </Form>
                        );
                    }}
                </OverlayPage>
            )}
        </PageController>
    );
}
